import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import classNames from "classnames/bind";
import { Button, Input, Radio, Tooltip } from "antd";
import { IconMoney, Logo } from "../../../assets/svg/logo";
import { useRecruitment } from "../../../context/RecruitmentContext";
import styles from "./ChatBotRecruiment.module.sass";
import {
  EXPERIENCE_OPTIONS,
  EDUCATION_OPTIONS,
  SALARY_OPTIONS,
  matchesSalaryFilter,
} from "../../../utils/constants/config";
import HeaderChatCta from "../../../layouts/desktop/Header/HeaderChatCta";

const cx = classNames.bind(styles);

const PageHeader = () => {
  const navigate = useNavigate();
  const {
    searchInput,
    setSearchInput,
    handleSearch,
    requestChatAccess,
    isCandidateRegistered,
  } = useRecruitment();

  const handleOpenChat = () => {
    if (requestChatAccess(null)) {
      navigate("/chat");
    }
  };

  return (
    <header className={cx("pageHeader")}>
      <div className={cx("headerBrand")} onClick={() => navigate("/")}>
        <div className={cx("logoBox")}>
          <Logo />
        </div>
        <div className={cx("headerTitle")}>
          <h1>Trợ lý tuyển dụng AI</h1>
          <p>Tư vấn vị trí phù hợp và hỗ trợ tuyển nhanh</p>
        </div>
      </div>
      <div className={cx("headerActions")}>
        {!isCandidateRegistered && (
          <HeaderChatCta onClick={handleOpenChat} />
        )}
        <div className={cx("headerSearch")}>
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Tìm kiếm vị trí"
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            style={{ width: "400px" }}
            allowClear
          />
          <Button type="primary" onClick={handleSearch}>
            Tìm kiếm
          </Button>
        </div>
      </div>
    </header>
  );
};

const FilterSidebar = () => {
  const {
    experienceFilter,
    setExperienceFilter,
    salaryFilter,
    setSalaryFilter,
    educationFilter,
    setEducationFilter,
  } = useRecruitment();

  return (
    <aside className={cx("filterSidebar")}>
      <div className={cx("filterGroup")}>
        <h3>Kinh nghiệm</h3>
        <Radio.Group
          className={cx("filterOptions")}
          value={experienceFilter}
          onChange={(e) => setExperienceFilter(e.target.value)}
        >
          {EXPERIENCE_OPTIONS.map((option) => (
            <Radio
              key={String(option.value)}
              value={option.value}
              className={cx("filterOption")}
            >
              {option.label}
            </Radio>
          ))}
        </Radio.Group>
      </div>
      <div className={cx("filterDivider")} />
      <div className={cx("filterGroup")}>
        <h3>Mức lương</h3>
        <Radio.Group
          className={cx("filterOptions")}
          value={salaryFilter}
          onChange={(e) => setSalaryFilter(e.target.value)}
        >
          {SALARY_OPTIONS.map((option) => (
            <Radio key={option} value={option} className={cx("filterOption")}>
              {option}
            </Radio>
          ))}
        </Radio.Group>
      </div>
      <div className={cx("filterDivider")} />
      <div className={cx("filterGroup")}>
        <h3>Trình độ học vấn</h3>
        <Radio.Group
          className={cx("filterOptions")}
          value={educationFilter}
          onChange={(e) => setEducationFilter(e.target.value)}
        >
          {EDUCATION_OPTIONS.map((option) => (
            <Radio
              key={String(option.value)}
              value={option.value}
              className={cx("filterOption")}
            >
              {option.label}
            </Radio>
          ))}
        </Radio.Group>
      </div>
    </aside>
  );
};

const LIST_META_FIELDS = [
  // { label: "Địa điểm", key: "location" },
  { label: "Số lượng cần tuyển", key: "quantity" },
  { label: "Kinh nghiệm", key: "experience" },
  { label: "Trình độ học vấn", key: "education" },
];

const SalaryBadge = ({ salaryRange, width }) => (
  <div
    className={cx("salaryBadgeGreen", "listSalaryBadge")}
    style={width ? { width } : undefined}
  >
    <IconMoney />
    <span>{salaryRange}</span>
  </div>
);

const useSalaryBadgeWidth = (campaigns) => {
  const measureRef = useRef(null);
  const [width, setWidth] = useState(undefined);

  const salaryRanges = useMemo(
    () => [...new Set(campaigns.map((item) => item.salaryRange ?? ""))],
    [campaigns],
  );

  useLayoutEffect(() => {
    const container = measureRef.current;
    if (!container) {
      setWidth(undefined);
      return;
    }

    const badges = container.querySelectorAll("[data-salary-measure]");
    const maxWidth = Array.from(badges).reduce(
      (max, badge) => Math.max(max, badge.offsetWidth),
      0,
    );
    setWidth(maxWidth || undefined);
  }, [salaryRanges]);

  const measureNode =
    salaryRanges.length > 0 ? (
      <div
        ref={measureRef}
        className={cx("salaryBadgeMeasure")}
        aria-hidden="true"
      >
        {salaryRanges.map((salaryRange) => (
          <div
            key={salaryRange}
            data-salary-measure
            className={cx("salaryBadgeGreen", "listSalaryBadge")}
          >
            <IconMoney />
            <span>{salaryRange}</span>
          </div>
        ))}
      </div>
    ) : null;

  return { width, measureNode };
};

const ListJobCard = ({ campaign, onClick, salaryBadgeWidth }) => (
  <div type="text" className={cx("listJobCard")} onClick={onClick}>
    <div className={cx("listJobCardBody")}>
      <h3>{campaign.title}</h3>
      <p className={cx("departmentLabel")}>{campaign.department}</p>
      <div className={cx("inlineMeta")}>
        {LIST_META_FIELDS.map((field) => {
          const value = campaign[field.key] ?? "";
          const tooltipTitle = `${field.label}: ${value}`;
          return (
            <div key={field.key} className={cx("metaCol")}>
              <Tooltip title={tooltipTitle} placement="top">
                <div className={cx("metaCell")}>
                  <span className={cx("metaLabel")}>{field.label}</span>
                  <strong className={cx("metaValue")}>{value}</strong>
                </div>
              </Tooltip>
            </div>
          );
        })}
      </div>
    </div>
    <SalaryBadge salaryRange={campaign.salaryRange} width={salaryBadgeWidth} />
  </div>
);

const CampaignListPage = () => {
  const navigate = useNavigate();
  const { campaigns, loading, enterCampaignFromList, salaryFilter } =
    useRecruitment();

  const filteredCampaigns = useMemo(
    () =>
      campaigns.filter((item) =>
        matchesSalaryFilter(item.salaryRange, salaryFilter),
      ),
    [campaigns, salaryFilter],
  );

  const { width: salaryBadgeWidth, measureNode: salaryBadgeMeasure } =
    useSalaryBadgeWidth(filteredCampaigns);

  const handleOpenCampaign = (campaignId) => {
    enterCampaignFromList(campaignId);
    navigate(`/${campaignId}`);
  };

  return (
    <>
      <PageHeader />
      <div className={cx("listBody")}>
        <FilterSidebar />
        <main className={cx("listMain")}>
          {salaryBadgeMeasure}
          {loading ? (
            <div className={cx("emptyList")}>
              <p>Đang tải danh sách vị trí...</p>
            </div>
          ) : filteredCampaigns.length > 0 ? (
            filteredCampaigns.map((item) => (
              <ListJobCard
                key={item.id}
                campaign={item}
                salaryBadgeWidth={salaryBadgeWidth}
                onClick={() => handleOpenCampaign(item.id)}
              />
            ))
          ) : (
            <div className={cx("emptyList")}>
              <p>Không tìm thấy vị trí phù hợp với bộ lọc.</p>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default CampaignListPage;
